import { useLayoutEffect, useRef, useState } from 'react';
import { FormControl, MenuItem, Select, useMediaQuery, useTheme } from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import { brandColors } from 'types/colors';

const DropdownMenuVertical = ({
  handleChange,
  selectedOption,
  options,
  labelName,
  disabled,
  width = '100%',
  menuScrollbarSx,
  shrinkLabel = false,
  emptyDisplayValue = '',
}) => {
  const anchorRef = useRef(null);
  const lastMeasuredWidthRef = useRef(0);
  const [menuWidth, setMenuWidth] = useState();
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));

  useLayoutEffect(() => {
    if (!anchorRef.current) return;
    let rafId = null;

    const update = () => {
      if (!anchorRef.current) return;
      const nextWidth = Math.round(anchorRef.current.getBoundingClientRect().width);
      if (nextWidth <= 0 || nextWidth === lastMeasuredWidthRef.current) return;
      lastMeasuredWidthRef.current = nextWidth;
      setMenuWidth(prev => (prev === nextWidth ? prev : nextWidth));
    };

    const scheduleUpdate = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        update();
      });
    };

    update();
    const ro = new ResizeObserver(scheduleUpdate);
    ro.observe(anchorRef.current);
    return () => {
      ro.disconnect();
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, []);

  if (!options || options.length === 0) {
    return (
      <FormControl ref={anchorRef} sx={{ width, py: 0 }}>
        <InputLabel shrink={shrinkLabel || Boolean(emptyDisplayValue) || undefined}>{labelName}</InputLabel>
        <Select disabled displayEmpty value="" label={labelName} renderValue={() => emptyDisplayValue} />
      </FormControl>
    );
  }

  return (
    <FormControl ref={anchorRef} sx={{ width, py: 0 }}>
      <InputLabel shrink={shrinkLabel || undefined}>{labelName}</InputLabel>
      <Select
        disabled={disabled}
        displayEmpty
        value={selectedOption || ''}
        label={labelName}
        onChange={handleChange}
        MenuProps={{
          disableScrollLock: true,
          marginThreshold: isXsScreen ? 0 : 16,
          PaperProps: {
            sx: theme => ({
              ...menuScrollbarSx,
              width: menuWidth, // <-- match Select width
              maxWidth: menuWidth, // optional: prevent stretching
              mt: '-15px',
              [theme.breakpoints.only('xs')]: {
                boxSizing: 'border-box',
                px: 0,
              },
            }),
          },
          MenuListProps: {
            sx: theme => ({
              maxHeight: 300,
              overflowY: 'auto',
              ...menuScrollbarSx,
              [theme.breakpoints.only('xs')]: {
                px: 1,
              },
            }),
          },
        }}>
        {options.map((o, i) => (
          <MenuItem
            key={`${labelName}-${o.value}-${i}`}
            value={o.value}
            sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              color: o.color,
              '&.Mui-selected, &.Mui-selected:hover': {
                backgroundColor: brandColors.BLUE,
                color: '#fff',
              },
            }} // wrap long labels
          >
            {o.label || o.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default DropdownMenuVertical;
